import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ConsentCheckboxes } from "@/components/auth/ConsentCheckboxes";
import { EMPTY_CONSENT, type ConsentChoices } from "@/lib/consent";
import { useLanguage } from "@/contexts/LanguageContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useAuth } from "@/hooks/useAuth";
import { useConsentStatus, useRecordConsent } from "@/hooks/useConsent";

/** Pages that capture consent themselves; the gate stays away from them. */
const CONSENT_CAPTURE_PATHS = ["/auth", "/login", "/owner-auth", "/invite/", "/admin-invite"];

/**
 * Asks a signed-in user to accept the Terms and Privacy Policy when they have
 * not accepted the CURRENT versions (first visit after a new version is
 * published, or an account that never ticked the box). It cannot be
 * dismissed: the only ways out are accepting or signing out.
 *
 * The marketing box is shown only to users who never answered it. Skipped
 * while a super-admin impersonates someone (their tick is not the user's),
 * and on the sign-in / sign-up / invite pages, which capture consent
 * themselves (asking there too would race their own write).
 */
export function ConsentReconfirmGate() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const { signOut } = useAuth();
  const { isImpersonating } = useImpersonation();
  const onConsentPage = CONSENT_CAPTURE_PATHS.some((p) => pathname.startsWith(p));
  const { data: status } = useConsentStatus({ enabled: !onConsentPage && !isImpersonating });
  const recordConsent = useRecordConsent();
  const [choices, setChoices] = useState<ConsentChoices>(EMPTY_CONSENT);

  if (onConsentPage || isImpersonating || !status?.needsReconfirm) return null;

  const askMarketing = !status.marketingAnswered;

  const accept = () =>
    recordConsent.mutate(
      {
        purpose: "login_reconfirm",
        channel: "reconfirm",
        ...(askMarketing ? { marketing: choices.marketing } : {}),
      },
      {
        onError: () =>
          toast.error(t("تعذّر حفظ موافقتك، حاول مرة أخرى", "Your consent could not be saved. Please try again.")),
      },
    );

  return (
    <AlertDialog open>
      <AlertDialogContent data-testid="consent-reconfirm-dialog" onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {status.hasConsent
              ? t("حدّثنا الشروط والأحكام وسياسة الخصوصية", "We've updated our Terms and Privacy Policy")
              : t("الموافقة على الشروط وسياسة الخصوصية", "Accept the Terms and Privacy Policy")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "راجع النسخة الحالية ووافق عليها لمتابعة استخدام دلّالي.",
              "Review the current version and accept it to keep using Dallali.",
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ConsentCheckboxes
          value={choices}
          onChange={setChoices}
          showMarketing={askMarketing}
          idPrefix="consent-reconfirm"
          disabled={recordConsent.isPending}
        />
        <AlertDialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => void signOut()} disabled={recordConsent.isPending}>
            {t("تسجيل الخروج", "Log out")}
          </Button>
          <Button onClick={accept} disabled={!choices.terms || recordConsent.isPending} data-testid="consent-reconfirm-accept">
            {recordConsent.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t("موافق ومتابعة", "Accept and continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
